import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, authedFileUrl } from "../api";
import {
  LayoutDashboard, Users, Truck, Calculator, CalendarClock, FileText,
  BarChart3, Download, History, UserPlus, Receipt, Settings as SettingsIcon,
  LogOut, Menu, X, CreditCard,
} from "lucide-react";
import SetupWizard from "./SetupWizard";
import EmailBackfillModal from "./EmailBackfillModal";

const navSections = [
  {
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
      { to: "/drivers", label: "Drivers", icon: Users, testId: "nav-drivers", perm: "drivers" },
      { to: "/trips", label: "Trips", icon: Truck, testId: "nav-trips", perm: "trips" },
      { to: "/pay-calculator", label: "Pay Calculator", icon: Calculator, testId: "nav-pay-calculator" },
      { to: "/pay-run", label: "Pay Run", icon: CalendarClock, testId: "nav-pay-run", perm: "payrun" },
      { to: "/pay-statements", label: "Pay Statements", icon: FileText, testId: "nav-pay-statements", permAny: ["payrun", "audit"] },
      { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports", perm: "reports" },
      { to: "/payroll-export", label: "Payroll Export", icon: Download, testId: "nav-payroll-export", perm: "reports" },
      { to: "/audit-trail", label: "Audit Trail", icon: History, testId: "nav-audit", perm: "audit" },
      { to: "/team", label: "Team & Access", icon: UserPlus, testId: "nav-team" },
      { to: "/deductions", label: "Deductions Library", icon: Receipt, testId: "nav-deductions", perm: "settings" },
      { to: "/expenses", label: "Driver Expenses", icon: Receipt, testId: "nav-expenses", perm: "expenses" },
      { to: "/billing", label: "Billing", icon: CreditCard, testId: "nav-billing", adminOnly: true },
      { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings", perm: "settings" },
    ],
  },
];

/** Returns true if the current user may see a nav item.
 *  - admin / superadmin see everything.
 *  - staff see only items whose `perm` (or any `permAny` entry) is in their permissions.
 *  - items marked `adminOnly` are hidden from staff regardless.
 */
function canSeeNavItem(item, user) {
  if (!user) return false;
  if (user.role === "admin" || user.role === "superadmin") return true;
  if (item.adminOnly) return false;
  const perms = user.permissions || [];
  if (item.perm) return perms.includes(item.perm);
  if (item.permAny) return item.permAny.some((p) => perms.includes(p));
  return true;  // ungated items (Dashboard, Pay Calculator, Team) stay visible
}

export default function Layout() {
  const { user, logout, billing, refreshUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoKey, setLogoKey] = useState(0);
  // Pop the Setup Wizard once per browser session when the company profile is
  // blank -- buyers get the 3-step onboarding on first login.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardDismissed, setWizardDismissed] = useState(
    () => sessionStorage.getItem("setup_wizard_dismissed") === "1"
  );
  const loc = useLocation();

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setLogoKey((v) => v + 1);
      if (!wizardDismissed && !(r.data.company_name || "").trim()) {
        setWizardOpen(true);
      }
    }).catch(() => {});
  }, [loc.pathname, wizardDismissed]);

  const closeWizard = () => {
    setWizardOpen(false);
    sessionStorage.setItem("setup_wizard_dismissed", "1");
    setWizardDismissed(true);
  };
  const completeWizard = () => {
    // Refresh so the new branding flows into the header/login.
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };

  const logoUrl = settings?.logo_path ? `${authedFileUrl("/settings/logo")}&v=${logoKey}` : null;
  const pageTag = (() => {
    const seg = loc.pathname.split("/")[1] || "";
    const map = {
      "": "Dashboard", drivers: "Drivers", trips: "Trips",
      "pay-calculator": "Pay Calculator", "pay-run": "Pay Run",
      "pay-statements": "Pay Statements", reports: "Reports",
      "payroll-export": "Payroll Export", "audit-trail": "Audit Trail",
      team: "Team & Access", deductions: "Deductions Library", settings: "Settings",
    };
    return map[seg] || "Dashboard";
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Trial countdown banner -- only shows when no sub and <= 7 days left */}
      {billing && !billing.has_active_subscription && !billing.trial_expired && billing.trial_days_left <= 7 && (
        <Link
          to="/billing"
          className="block bg-amber-500 text-white text-center text-xs font-semibold py-2 px-4 hover:bg-amber-600"
          data-testid="trial-banner"
        >
          {billing.trial_days_left === 0
            ? "Trial ends today — subscribe to keep your data"
            : `Trial ends in ${billing.trial_days_left} day${billing.trial_days_left === 1 ? "" : "s"} — subscribe to keep access`}
          <span className="underline ml-2">Upgrade now →</span>
        </Link>
      )}
      {billing && billing.subscription_status === "past_due" && (
        <Link
          to="/billing"
          className="block bg-rose-600 text-white text-center text-xs font-semibold py-2 px-4 hover:bg-rose-700"
          data-testid="pastdue-banner"
        >
          Payment past due — update your card to avoid losing access.
          <span className="underline ml-2">Manage billing →</span>
        </Link>
      )}
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 bg-white border-r border-slate-200 flex flex-col transition-transform lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 px-4 flex items-center justify-between border-b border-slate-100">
          <Link to="/" className="flex items-center gap-2.5 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt={settings?.company_name || "Logo"}
                   className="w-9 h-9 rounded object-contain bg-white border border-slate-200"
                   data-testid="sidebar-logo-img" />
            ) : (
              <div className="w-8 h-8 rounded bg-slate-900 grid place-items-center text-white text-xs" data-testid="sidebar-logo-fallback">
                {(settings?.company_name || "").trim().slice(0,2).toUpperCase() || <Truck size={14} />}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">
                {settings?.company_name || "Driver Pay & Trip Manager"}
              </div>
              <div className="text-[10px] text-slate-500 truncate">Driver Pay &amp; Trip Manager</div>
            </div>
          </Link>
          <button className="lg:hidden text-slate-500" onClick={() => setMobileOpen(false)} data-testid="close-sidebar">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {navSections[0].items.filter((item) => canSeeNavItem(item, user)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-[13px] font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-slate-900 grid place-items-center text-white text-[11px] font-bold">
              {(user?.username || "A").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-900 truncate">{user?.username}</div>
              <div className="text-[10px] text-slate-500 truncate">{user?.role}</div>
            </div>
            <button
              onClick={logout}
              className="text-slate-400 hover:text-rose-600"
              data-testid="logout-btn"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-60">
        {/* Company header strip */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-full px-4 lg:px-8 py-3 flex items-center gap-4 flex-wrap">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="open-sidebar">
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded object-contain border border-slate-200 bg-white" />
              ) : (
                <div className="w-12 h-12 rounded bg-slate-900 grid place-items-center text-white text-xs">{(settings?.company_name || "").trim().slice(0,2).toUpperCase() || <Truck size={18} />}</div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{settings?.company_name || "Driver Pay & Trip Manager"}</span>
                  <span className="pill-gray">{pageTag}</span>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {settings?.address} · Phone: {settings?.phone}{settings?.email ? ` · ${settings.email}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Mobile-only quick logout. The sidebar logout exists too but
                  lives behind the hamburger menu; this gives staff / admin a
                  one-tap sign-out from any page on a phone. */}
              <button
                onClick={logout}
                className="lg:hidden inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-600 hover:text-rose-700 hover:bg-rose-50 border border-slate-200"
                data-testid="logout-btn-mobile"
                title="Sign out"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </div>
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <SetupWizard
        open={wizardOpen}
        settings={settings}
        onClose={closeWizard}
        onComplete={completeWizard}
      />
      <EmailBackfillModal user={user} onSaved={refreshUser} />
    </div>
  );
}
